import React, { useState } from 'react'

import { TURNSTILE_COOKIE_NAME, TURNSTILE_STATUSES } from '../../consts'
import { DISABLE_TURNSTILE } from '../../../../utils/consts.mjs'

/**
 * Context Provider Component for Managing Turnstile State and Behaviors.
 * This component acts as a higher-order component (HOC) that injects turnstile status and a handler function
 * into the wrapped component, enabling it to respond to turnstile events and manage related state.
 */
export const TurnstileProvider = ({ WrappedComponent, ...props }) => {
  const [turnstileStatus, setTurnstileStatus] = useState(null)
  /**
   * Handler Function for Updating Turnstile Status.
   * This function not only updates the turnstile status but also manages cookies based on the provided cookie action.
   * It ensures that the turnstile status reflects the latest verification state and allows for cookie manipulation
   * to persist or remove the turnstile token.
   *
   * @param {Object} args - An object containing the new status value and the cookie action ('add' or 'remove').
   * @param {String} args.value - The new status value for the turnstile.
   * @param {String} args.cookie - The cookie action to perform ('add' or 'remove').
   */
  const handleTurnstileStatus = ({ token, methods, args }) => {
    const [value, cookie] = args
    // Updates the turnstile status state with the new status, methods, and additional flags like verification status.
    setTurnstileStatus({
      token,
      methods: { ...methods, reload: () => window.location.reload() },
      status: value,
      isVerified: DISABLE_TURNSTILE || value === TURNSTILE_STATUSES.VERIFIED,
    })

    // Manages cookies based on the provided action, either adding or removing the turnstile token.
    switch (cookie) {
      case 'add':
        document.cookie = `${TURNSTILE_COOKIE_NAME}=${token}; path=/; max-age=3600`
        break
      default:
        document.cookie = `${TURNSTILE_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
        break
    }
  }

  // Renders the wrapped component, passing through all original props and injecting turnstile status and handler function.
  return (
    <WrappedComponent
      {...props}
      turnstileStatus={turnstileStatus}
      handleTurnstileStatus={handleTurnstileStatus}
    />
  )
}
